/**
 * 2D marker renderer using AdvancedMarkerElement + MarkerClusterer.
 * Markers are partitioned into named layers, each with its own clusterer
 * so layers can be toggled and rendered independently.
 */

import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { createMarkerElement } from '../marker-icons';
import { buildPopupHtml, isFeedTrack } from './marker-popup';
import { attachClassificationListeners } from './classification-popup';
import { selectEntity } from './selection';
import type { MarkerData, MarkerDiff } from './marker-state';

type Marker = google.maps.marker.AdvancedMarkerElement;

/** Per-layer marker registries keyed by layer, then by UID. */
const layerMarkers = new Map<string, Map<string, Marker>>();

/** Per-layer clusterer instances. */
const layerClusterers = new Map<string, MarkerClusterer>();

/** Active map reference (set on init). */
let activeMap: google.maps.Map | null = null;

/** Shared InfoWindow instance (reused for all popups). */
let infoWindow: google.maps.InfoWindow | null = null;

/** Initialize 2D marker system for a map */
export function initMarkers2D(map: google.maps.Map): void {
  activeMap = map;
  infoWindow = new google.maps.InfoWindow();
}

/** Apply a diff of markers for a layer to the 2D map */
export function applyLayerDiff2D(map: google.maps.Map, layer: string, diff: MarkerDiff): void {
  const registry = getLayerMarkers(layer);
  const clusterer = getLayerClusterer(layer, map);
  removeMarkers(diff.removed, registry, clusterer);
  reclassifyMarkers(map, diff.reclassified, registry, clusterer);
  createMarkers(map, diff.added, registry, clusterer);
  updatePositions(diff.updated, registry);
}

/** Re-create markers whose symbol changed (classification override) */
function reclassifyMarkers(
  map: google.maps.Map,
  markers: MarkerData[],
  registry: Map<string, Marker>,
  clusterer: MarkerClusterer,
): void {
  if (markers.length === 0) return;
  removeMarkers(markers.map((m) => m.uid), registry, clusterer);
  createMarkers(map, markers, registry, clusterer);
}

/** Set marker positions for a layer (used by animation frames) */
export function setLayerPositions2D(
  layer: string,
  positions: Map<string, google.maps.LatLngLiteral>,
): void {
  const registry = layerMarkers.get(layer);
  if (!registry) return;
  for (const [uid, pos] of positions) {
    const marker = registry.get(uid);
    if (marker) marker.position = pos;
  }
}

/** Show or hide a layer's markers */
export function setLayerVisibility2D(layer: string, visible: boolean): void {
  const target = visible ? activeMap : null;
  const registry = layerMarkers.get(layer);
  if (registry) {
    for (const marker of registry.values()) marker.map = target;
  }
  layerClusterers.get(layer)?.setMap(target);
}

/** Destroy all 2D markers, clusterers, and the shared InfoWindow */
export function destroyMarkers2D(): void {
  for (const registry of layerMarkers.values()) clearMarkerMap(registry);
  for (const clusterer of layerClusterers.values()) clusterer.clearMarkers();
  layerMarkers.clear();
  layerClusterers.clear();
  activeMap = null;
  infoWindow = null;
}

/* ─── Internal ─── */

/** Get (or create) the marker registry for a layer */
function getLayerMarkers(layer: string): Map<string, Marker> {
  let registry = layerMarkers.get(layer);
  if (!registry) {
    registry = new Map();
    layerMarkers.set(layer, registry);
  }
  return registry;
}

/** Get (or create) the clusterer for a layer */
function getLayerClusterer(layer: string, map: google.maps.Map): MarkerClusterer {
  let clusterer = layerClusterers.get(layer);
  if (!clusterer) {
    clusterer = new MarkerClusterer({ map, markers: [] });
    layerClusterers.set(layer, clusterer);
  }
  return clusterer;
}

function createMarkers(
  map: google.maps.Map,
  markers: MarkerData[],
  registry: Map<string, Marker>,
  clusterer: MarkerClusterer,
): void {
  const created: Marker[] = [];

  for (const data of markers) {
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: data.position,
      content: createMarkerElement(data.cotType, data.callsign, undefined, data.icon, data.simulated),
      title: data.callsign,
    });

    marker.addListener('click', () => openPopup(marker, map, data));
    registry.set(data.uid, marker);
    created.push(marker);
  }

  // Batch the clusterer insert — adding one-by-one re-clusters per call (O(n²)).
  if (created.length > 0) clusterer.addMarkers(created);
}

function removeMarkers(
  uids: string[],
  registry: Map<string, Marker>,
  clusterer: MarkerClusterer,
): void {
  const removed: Marker[] = [];

  for (const uid of uids) {
    const marker = registry.get(uid);
    if (!marker) continue;
    marker.map = null;
    registry.delete(uid);
    removed.push(marker);
  }

  if (removed.length > 0) clusterer.removeMarkers(removed);
}

function updatePositions(markers: MarkerData[], registry: Map<string, Marker>): void {
  for (const data of markers) {
    const marker = registry.get(data.uid);
    if (marker) marker.position = data.position;
  }
}

function openPopup(marker: Marker, map: google.maps.Map, data: MarkerData): void {
  if (!infoWindow) return;
  selectEntity(data);
  infoWindow.setContent(buildPopupHtml(data));
  infoWindow.open({ anchor: marker, map });
  if (isFeedTrack(data.layer)) {
    google.maps.event.addListenerOnce(infoWindow, 'domready', () =>
      attachClassificationListeners(data.uid),
    );
  }
}

function clearMarkerMap(registry: Map<string, Marker>): void {
  for (const marker of registry.values()) marker.map = null;
  registry.clear();
}
